// Extra JSON-LD nodes for the public marketing pages, added to RouteMeta's @graph (Organization + the WebPage /
// Article node):
//
//   * BreadcrumbList on every page but the home page: Photohawk → the page.
//   * FAQPage where the page visibly shows questions and answers: /for-participants, /preview-first-uploads and
//     /pricing-changes-2026. The answers are in the server-rendered HTML (the accordions force-mount them), and
//     the FAQPage is built from the same copy the page renders, in the page's language.
//   * Product with an AggregateOffer of today's plans on /pricing, from TODAY_PLANS (src/content/pricing/plans.ts),
//     the same numbers as the plan cards. At the 1 November 2026 launch, when /pricing shows the new plans,
//     switch this to NEW_PLANS (src/lib/seo/structuredData.test.ts checks every price is on the page).
//
// Never a rating or review: Photohawk has no review data to mark up.

import { getPreviewFirstCopy, PREVIEW_FIRST_PATH } from "@/content/previewFirst";
import { getPricingCopy } from "@/content/pricing";
import { PRICING_PATHS, TODAY_PLANS } from "@/content/pricing/plans";
import { PARTICIPANT_FAQ, type FaqItem } from "@/content/forParticipants/faq";
import type { SeoLang } from "./site";

export interface StructuredDataInput {
  origin: string;
  /** The page's canonical path. */
  path: string;
  /** This URL (canonical, with ?lang= when localised). */
  url: string;
  /** The language the page is shown in. */
  lang: SeoLang;
  title: string;
  description: string;
}

/** The questions and answers a page shows, in a language, if it has an FAQ. */
export const pageFaq = (path: string, lang: SeoLang): FaqItem[] | null => {
  if (path === "/for-participants") return PARTICIPANT_FAQ;
  if (path === PREVIEW_FIRST_PATH) return getPreviewFirstCopy(lang).faq.items.map(({ q, a }) => ({ q, a }));
  if (path === PRICING_PATHS.changes) return getPricingCopy(lang).changes.faq.items.map(({ q, a }) => ({ q, a }));
  return null;
};

/** A breadcrumb name from a page title: the part before " | Photohawk" (or after "Photohawk | "). */
export const crumbName = (title: string): string => {
  const parts = title.split(" | ").map((s) => s.trim());
  if (parts.length < 2) return title;
  return parts[0] === "Photohawk" ? parts[1] : parts[0];
};

const breadcrumbLd = ({ origin, url, title }: StructuredDataInput) => ({
  "@type": "BreadcrumbList",
  "@id": `${url}#breadcrumb`,
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Photohawk", item: `${origin}/` },
    { "@type": "ListItem", position: 2, name: crumbName(title), item: url },
  ],
});

const faqLd = (url: string, items: FaqItem[]) => ({
  "@type": "FAQPage",
  "@id": `${url}#faq`,
  mainEntity: items.map((item) => ({
    "@type": "Question",
    name: item.q,
    acceptedAnswer: { "@type": "Answer", text: item.a },
  })),
});

const usd = (n: number) => n.toFixed(2);

/** Today's plans as offers (prices a month, US dollars). */
export const pricingOfferLd = ({ origin, url, description }: StructuredDataInput) => {
  const prices = TODAY_PLANS.map((p) => p.priceUsd);
  return {
    "@type": "Product",
    "@id": `${origin}/pricing#product`,
    name: "Photohawk",
    description,
    brand: { "@id": `${origin}/#organization` },
    category: "Event photo sharing and selling software",
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "USD",
      lowPrice: usd(Math.min(...prices)),
      highPrice: usd(Math.max(...prices)),
      offerCount: TODAY_PLANS.length,
      offers: TODAY_PLANS.map((p) => ({
        "@type": "Offer",
        name: p.name,
        url,
        price: usd(p.priceUsd),
        priceCurrency: "USD",
        priceSpecification: {
          "@type": "UnitPriceSpecification",
          price: usd(p.priceUsd),
          priceCurrency: "USD",
          unitCode: "MON",
          referenceQuantity: { "@type": "QuantitativeValue", value: 1, unitCode: "MON" },
        },
      })),
    },
  };
};

/** The extra nodes for a page (see the top of this file). Pages with their own breadcrumbs (/guides, /blog, /event-photography) are skipped. */
export function extraStructuredData(input: StructuredDataInput): object[] {
  const nodes: object[] = [];
  const ownCrumbs = input.path.startsWith("/guides") || input.path.startsWith("/blog") || input.path.startsWith("/event-photography");
  if (input.path !== "/" && !ownCrumbs) nodes.push(breadcrumbLd(input));
  const faq = pageFaq(input.path, input.lang);
  if (faq?.length) nodes.push(faqLd(input.url, faq));
  if (input.path === "/pricing") nodes.push(pricingOfferLd(input));
  return nodes;
}
